/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState, useMemo } from 'react'
import {
  Box,
  CircularProgress,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Pagination,
  Menu,
  MenuItem,
  useTheme
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import {
  Edit as EditIcon,
  Clear as ClearIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  ExpandMore,
} from '@mui/icons-material'
import { useIntl } from 'react-intl'
import { API } from 'core/requests'
import { SparckTableProps } from '../sparckTable/SparckTable.types'
import { getSwitch } from 'utils/tables/dataTypes'
import { getAccordionConfig } from './SparckAccordionConfig/SparckAccordionConfig'
import { Theme } from '@mui/material'
import type { SxProps } from '@mui/system'
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import { SelectChangeEvent } from '@mui/material/Select'
import BudgetSummaryChart from './BudgetSummaryChart/BudgetSummaryChart'
import ExportData from './ExportData/ExportData'
import { Column } from '@material-table/core'

interface BudgetItem {
  id: number
  label: string
  amount: string
  parent_id?: number
  parentId?: number
}

const useStyles = makeStyles((theme: Theme) => ({
  gridContainer: {
    '& .MuiToolbar-root': {
      flexDirection: 'column',
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
      },
    },
  },
  accordion: {
    marginBottom: theme.spacing(1),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.paper,
  },
  accordionDetails: {
    padding: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(2),
  },
  paginationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  rowsPerPageSelect: {
    minWidth: 120,
  },
  pagination: {
    '& .MuiPaginationItem-root': {
      margin: '0 2px',
      minWidth: 28,
      height: 28,
    },
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),
  },
  summaryField: {
    flex: 1,
    minWidth: 0,
  },
  detailsField: {
    marginBottom: theme.spacing(2),
  },
  actionMenu: {
    '& .MuiMenuItem-root': {
      padding: theme.spacing(1, 2),
    },
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    '& .MuiTypography-root': {
      fontSize: '0.875rem',
      whiteSpace: 'normal',
      lineHeight: 1.2,
    }
  },
  menuIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    marginRight: theme.spacing(1),
    flexShrink: 0,
  },
  menuButton: {
    marginLeft: 'auto',
    flexShrink: 0,
    zIndex: 1,
  },
  searchField: {
    marginRight: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      height: 36,
    },
  },
  toolbarContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
}))

const SparckAccordion = (props: SparckTableProps) => {
  const intl = useIntl()
  const translate = id => intl.formatMessage({ id: id })
  const classes = useStyles()
  const [expanded, setExpanded] = useState<string | false>(false)
  const [rowData, setData] = useState<any>(props.data || [])
  const [employees, setEmployees] = useState<any>([])
  const [searchText, setSearchText] = useState('')
  const theme = useTheme()
  const [isExporting, setIsExporting] = useState(false)
  const [employeesTotalCount, setEmployeesTotalCount] = useState(0)

  const {
    slug,
    data = [] as Record<any, unknown>[],
    columns,
    setPagination,
    editAction,
    deleteAction,
    actions = [],
    updateItem,
    hasStatusSwitch,
    hideToolbar,
    totalCount,
    setSearchText: setSearchTextProp,
    pagination: parentPagination,
  } = props

  // console.log('slug', slug)
  // console.log('props', props)
  // console.log('data', data)

  const config = getAccordionConfig(slug)
  config.columns = columns as Column<Record<string, unknown>>[]

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    if (setPagination && parentPagination) {
      setPagination({
        pageSize: parentPagination.pageSize,
        currentPage: value - 1
      })
    }
  }

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    if (setPagination && parentPagination) {
      setPagination({
        pageSize: Number(event.target.value),
        currentPage: 0
      })
    }
  }

  const calculateTotalPages = () => {
    if (slug === 'employees') {
      return Math.ceil(employeesTotalCount / (parentPagination?.pageSize || 10))
    }
    return Math.ceil((totalCount || data.length) / (parentPagination?.pageSize || 10))
  }

  const getPaginatedData = () => {
    if (slug === 'employees') {
      return employees
    }

    let filteredData = data
    if (searchText) {
      filteredData = filterData(data, searchText)
    }

    if (!parentPagination) return filteredData

    const startIndex = parentPagination.currentPage * parentPagination.pageSize
    const endIndex = startIndex + parentPagination.pageSize
    return filteredData.slice(startIndex, endIndex)
  }

  const renderSwitch = (row: any) => getSwitch(row, 'status', updateItem)

  if (hasStatusSwitch && !columns.find(column => column.field === 'status')) {
    columns.push({
      title: 'Status',
      field: 'status',
      render: renderSwitch,
      editable: 'never' as any,
    })
  }

  const getEmployees = async (currentPage = 0, pageSize = 10, search = '') => {
    try {
      const offset = currentPage * pageSize
      const response = await API.get('employees', null, {
        queryParams: {
          limit: pageSize,
          offset: offset,
          search: search,
        },
      })
      setEmployees(response.users)
      setData(response.users)
      setEmployeesTotalCount(response.users_count || 0)
      return response
    } catch (error) {
      console.error('Error fetching employees:', error)
      return { users: [], users_count: 0 }
    }
  }

  useEffect(() => {
    if (slug === 'employees' && parentPagination) {
      getEmployees(parentPagination.currentPage, parentPagination.pageSize, searchText)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, parentPagination?.currentPage, parentPagination?.pageSize, searchText])

  const filterData = (data: any[], searchText: string) => {
    if (!searchText) return data

    return data.filter(row => {
      return Object.values(row).some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchText.toLowerCase())
        }
        return false
      })
    })
  }

  useEffect(() => {
    if (slug !== 'employees') {
      setData(data)
    }
  }, [data, slug])

  useEffect(() => {
    if (parentPagination) {
      localStorage.setItem(`${slug}PageSize`, parentPagination?.pageSize.toString())
    }
  }, [parentPagination, slug])

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const renderFieldValue = (field: string, value: any, row: any) => {
    if (config.renderField) {
      const renderedValue = config.renderField(field, value, theme, row)
      return renderedValue;
    }
    return value
  }

  const getNestedValue = (obj: any, field: string) => {
    const fields = field.split('.')
    let value = obj
    for (const f of fields) {
      if (value && typeof value === 'object' && f in value) {
        value = value[f]
      } else {
        return undefined
      }
    }
    return value
  }

  const shouldShowPagination = useMemo(() => {
    const hidePaginationSlugs = [
      'budget-summary-report',
      'summary-report'
    ]
    return !hidePaginationSlugs.includes(slug)
  }, [slug])

  return (
    <Grid container className={classes.gridContainer}>
      <Grid item xs={12}>
        {!hideToolbar && slug != 'budget-summary-report' && (
          <Box
            className={classes.toolbarContainer}
            sx={{ mt: slug === 'employees' ? 2 : 0 }}
          >
            <TextField
              className={classes.searchField}
              size="small"
              placeholder={translate('table.toolbar.searchPlaceholder')}
              value={searchText}
              onChange={(e) => {
                const value = e.target.value
                setSearchText(value)
                if (setSearchTextProp) {
                  setSearchTextProp(value)
                }
                if (setPagination && parentPagination) {
                  const newPagination = {
                    pageSize: parentPagination.pageSize,
                    currentPage: 0
                  }
                  setPagination(newPagination)
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchText('');
                        if (setSearchTextProp) {
                          setSearchTextProp('')
                        }
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <ExportData
                slug={slug}
                data={slug === 'employees' ? employees : data}
                columns={columns}
                onExportStart={() => setIsExporting(true)}
                onExportEnd={() => setIsExporting(false)}
              />
              {isExporting && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.5)',
                  zIndex: 2
                }}>
                  <CircularProgress size={32} />
                </Box>
              )}
            </Box>
          </Box>
        )}

        {slug === 'budget-summary-report' ? (
          <BudgetSummaryChart
            data={data as BudgetItem[]}
          />
        ) : (
          getPaginatedData().map((row: any, index: number) => {
            return (
              <Accordion
                key={row.id || index}
                expanded={expanded === `panel${index}`}
                onChange={handleChange(`panel${index}`)}
                className={classes.accordion}
                sx={{
                  ...(config.style as SxProps<Theme>),
                  '& .MuiAccordionDetails-root': {
                    backgroundColor: '#f5f5f5',
                    padding: '16px',
                    borderRadius: '0 0 4px 4px'
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  className={classes.accordionSummary}
                  id={`accordion-summary-${row.id || index}`}
                >
                  <Box className={classes.summaryContent}>
                    {config.summaryColumns.map((field: string, colIndex: number) => {
                      const column = columns.find(col => col.field === field)

                      if (!column) return null

                      const nestedValue = getNestedValue(row, field)

                      return (
                        <Box key={colIndex} className={classes.summaryField}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {slug != 'top-active-users' && slug != 'profile-report' && (column.title)}
                          </Typography>
                          <Typography variant="body1" style={{ whiteSpace: 'normal', lineHeight: 1.2 }}>
                            {config.renderField
                              ? config.renderField(field, nestedValue, theme, row)
                              : nestedValue}
                          </Typography>
                        </Box>
                      )
                    })}
                    {config.showActionMenu !== false && (
                      <Box className={classes.menuButton}>
                        <PopupState variant="popover" popupId={`actions-popup-${row.id || index}`}>
                          {(popupState) => (
                            <div>
                              <IconButton
                                {...bindTrigger(popupState)}
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  popupState.open(e.currentTarget)
                                }}
                                size="small"
                                aria-controls={popupState.isOpen ? `actions-menu-${row.id || index}` : undefined}
                                aria-haspopup="true"
                                aria-expanded={popupState.isOpen ? 'true' : undefined}
                              >
                                <MoreVertIcon />
                              </IconButton>
                              <Menu
                                {...bindMenu(popupState)}
                                className={classes.actionMenu}
                                onClick={(e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                }}
                                anchorOrigin={{
                                  vertical: 'center',
                                  horizontal: 'right',
                                }}
                                transformOrigin={{
                                  vertical: 'top',
                                  horizontal: 'right',
                                }}
                                PaperProps={{
                                  onClick: (e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                  },
                                  sx: {
                                    ...(slug === 'shareReports' && {
                                      maxWidth: 'calc(100vw - 32px)',
                                    }),
                                  },
                                }}
                              >
                                {editAction && !editAction.hidden?.(row) && (
                                  <MenuItem
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      editAction.onClick(row)
                                      popupState.close()
                                    }}
                                    disabled={editAction.disabled?.(row)}
                                    className={classes.menuItem}
                                  >
                                    <Box className={classes.menuIcon}>
                                      <EditIcon color={editAction.disabled?.(row) ? 'disabled' : 'primary'} />
                                    </Box>
                                    <Typography variant="body2" noWrap>
                                      {translate('table.body.editTooltip')}
                                    </Typography>
                                  </MenuItem>
                                )}
                                {deleteAction && !deleteAction.hidden?.(row) && (
                                  <MenuItem
                                    onClick={(e) => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      deleteAction.onClick(row)
                                      popupState.close()
                                    }}
                                    disabled={deleteAction.disabled?.(row)}
                                    className={classes.menuItem}
                                  >
                                    <Box className={classes.menuIcon}>
                                      <ClearIcon color={deleteAction.disabled?.(row) ? 'disabled' : 'error'} />
                                    </Box>
                                    <Typography variant="body2" noWrap>
                                      {translate('table.body.deleteTooltip')}
                                    </Typography>
                                  </MenuItem>
                                )}
                                {actions
                                  .filter(action => !action.isFreeAction)
                                  .map((action: any, actionIndex: number) => (
                                    !action.hidden?.(row) && (
                                      <MenuItem
                                        key={actionIndex}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          action.onClick?.(e, row)
                                          popupState.close()
                                        }}
                                        disabled={action.disabled?.(row)}
                                        className={classes.menuItem}
                                        sx={{
                                          ...(slug === 'shareReports' && {
                                            minWidth: '220px',
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                          })
                                        }}
                                      >
                                        <Box
                                          className={classes.menuIcon}
                                          sx={{
                                            ...(slug === 'shareReports' && {
                                              width: 'auto',
                                              height: 'auto',
                                              minWidth: '24px',
                                              flex: 1,
                                              display: 'flex',
                                              justifyContent: 'flex-start',

                                              marginRight: '12px',
                                              flexShrink: 0
                                            })
                                          }}
                                        >
                                          {action.icon && action.icon(row)}
                                        </Box>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            ...(slug === 'shareReports' && {
                                              flex: action.tooltip?.(row) ? 1 : 0,
                                              textAlign: 'left',
                                              overflow: 'visible',
                                              fontSize: '0.875rem',
                                              lineHeight: '1.3'
                                            })
                                          }}
                                        >
                                          {action.tooltip?.(row)}
                                        </Typography>
                                      </MenuItem>
                                    )
                                  ))}
                              </Menu>
                            </div>
                          )}
                        </PopupState>
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  {config.customDetailsRenderer ? (
                    config.customDetailsRenderer(row, config, classes)
                  ) : (
                    config.detailsColumns.map((field: string, colIndex: number) => {
                      const column = columns.find(col => col.field === field)

                      if (!column) {
                        if (row[field] !== undefined) {
                          const title = field.split('_').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')

                          const renderedValue = renderFieldValue(field, row[field], row)

                          return (
                            <Box key={colIndex} className={classes.detailsField}>
                              <Typography variant="subtitle2" color="textSecondary">
                                {title}
                              </Typography>
                              {renderedValue}
                            </Box>
                          )
                        }
                        return null
                      }

                      const renderedValue = column.render ? column.render(row, row) : renderFieldValue(field, row[field], row)

                      return (
                        <Box key={colIndex} className={classes.detailsField}>
                          <Typography variant="subtitle2" color="textSecondary">
                            {column.title}
                          </Typography>
                          {renderedValue}
                        </Box>
                      )
                    })
                  )}
                </AccordionDetails>
              </Accordion>
            )
          })
        )}

        {shouldShowPagination && parentPagination && (
          <Box className={classes.paginationContainer}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" className={classes.rowsPerPageSelect}>
                <InputLabel>{'rows'}</InputLabel>
                <Select
                  value={parentPagination.pageSize}
                  onChange={handleRowsPerPageChange}
                  label={'rows'}
                >
                  {[5, 10, 25, 50].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Pagination
              count={calculateTotalPages()}
              page={parentPagination.currentPage + 1}
              onChange={handlePageChange}
              color="primary"
              hidePrevButton={false}
              hideNextButton={false}
              showFirstButton={false}
              showLastButton={false}
              siblingCount={0}
              boundaryCount={0}
              className={classes.pagination}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  )
}

export default SparckAccordion
